import { Outlet, Link, useLocation } from 'react-router-dom';
import { GlazeModalProvider } from '@clay-run/glaze/GlazeProviders';
import { GlobalGlazeToastRegion } from '@clay-run/glaze/toast/toast-provider';
import { ThemeToggle } from '../../_shared/components';
import { useCurrentUser } from '../../_shared/hooks';
import { useAppBasePath } from '../../_shared/base-path';
import { isLauncherAdmin } from '../config-page-logic';
import {
  usePlaybooksDrawerStore,
  selectIsAnyDrawerOpen,
  mainContentPushClassName,
} from '../drawer-store';
import { UnsavedEditsPill } from './UnsavedEditsPill';
import { UsageSidebar } from './UsageSidebar';

/**
 * Root layout for the `/v2` route subtree — the foundation every other A2
 * unit builds on (U1).
 *
 * Mounts `GlobalGlazeToastRegion` (first usage anywhere in the repo). Glaze
 * setup (tokens, dark mode, fonts, `@source`) is already wired globally via
 * `web/app-home/src/index.css` — no per-component stylesheet import needed
 * here.
 *
 * Import-path note: `@clay-run/glaze/toast` has no flat `dist/toast.js`
 * (unlike most single-file Glaze modules) — its dist output stayed a
 * directory, so the package's `"./*": "./dist/*.js"` export map only
 * resolves the *nested* subpaths used above (`toast/toast-provider`,
 * `toast/toast-queue`), not a bare `@clay-run/glaze/toast` import. Verified
 * via direct Node ESM resolution against `node_modules/@clay-run/glaze` in
 * this worktree; recorded here (and as a discovery in the implementer's
 * notes) so nobody "fixes" this back to the barrel-looking form.
 *
 * `GlazeModalProvider` (U3): `useAlertDialog()`/`useDialogBase()` throw
 * unless a `DialogBaseProvider` (Glaze's own re-export name:
 * `GlazeModalProvider`, from `GlazeProviders.tsx`) is mounted above the
 * caller — verified against `node_modules/@clay-run/glaze/src/DialogBase.tsx`,
 * whose context default is a throwing stub specifically for this case. Zero
 * prior mount anywhere in the repo (same "first real usage" situation as the
 * toast region above), so U3's config-page Delete confirmation (the first
 * `AlertDialog` consumer) needs it mounted here, once, for every `/v2` page.
 */

export function PlaybooksLayout() {
  const basePath = useAppBasePath();
  const location = useLocation();
  const { roles } = useCurrentUser();
  const isAdmin = isLauncherAdmin(roles);
  const homePath = `${basePath}`;
  const configPath = `${basePath}/config`;
  const settingsPath = `${basePath}/settings`;
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`;
  const isConfig = location.pathname.startsWith(configPath);
  const isSettings = location.pathname.startsWith(settingsPath);

  /**
   * U4's push plumbing. A mounted `PushDrawer` registers itself in
   * `drawer-store`, and this container reserves the panel's width so the page
   * REFLOWS beside it rather than being covered — at every viewport width
   * (Decision 4; the prototype's sub-1400px overlay fallback is deliberately
   * not ported). Subscribing to the derived boolean, not the id list, keeps
   * this layout un-rendered when one drawer replaces another.
   */
  const isDrawerOpen = usePlaybooksDrawerStore(selectIsAnyDrawerOpen);
  const mainClassName = [
    'flex-1 overflow-y-auto transition-[padding-right] duration-200',
    mainContentPushClassName(isDrawerOpen),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <GlazeModalProvider>
      <div className="flex min-h-full flex-col bg-bg-primary text-content-primary">
        <header className="flex items-center justify-between gap-4 border-b border-border-primary px-6 py-3">
          <nav className="flex items-center gap-4" aria-label="Playbooks">
            {/*
              U7 (E3) — `data-playbooks-nav` is the handle `UnsavedWorkGuard`'s
              capture-phase click listener matches on. These two Links are the
              ONLY way out of an editing surface that this app controls (the
              `?step=` tabs go through `setSearchParams`, never a Link), so
              they are the only ones that need to ask before discarding work.
            */}
            <Link
              data-playbooks-nav
              to={homePath}
              className={
                isHome
                  ? 'text-sm font-semibold text-content-primary'
                  : 'text-sm font-medium text-content-tertiary hover:text-content-primary'
              }
            >
              Home
            </Link>
            <Link
              data-playbooks-nav
              to={configPath}
              className={
                isConfig
                  ? 'text-sm font-semibold text-content-primary'
                  : 'text-sm font-medium text-content-tertiary hover:text-content-primary'
              }
            >
              Library
            </Link>
            {/*
              U3 (playbooks-clay-wire-gaps plan) — a UI courtesy only, not
              real enforcement: the server-side `requireRole('launcher_admin',
              'auth')` on `PATCH /settings` is the actual gate — AUTH-scoped
              (Key Decision 5, A5 review finding: it shipped `'acting'`, since
              fixed), so the gate can't flip under impersonation — and the
              page component itself re-checks `isLauncherAdmin` for anyone who
              lands on the route directly. `isAdmin` here reads `roles` from
              `useCurrentUser()`, which is already the AUTH user's roles
              (`/api/me` → `getRoleSlugs(authContext, 'auth')`), so this nav
              check has always agreed with the server gate's scope.
            */}
            {isAdmin && (
              <Link
                data-playbooks-nav
                to={settingsPath}
                className={
                  isSettings
                    ? 'text-sm font-semibold text-content-primary'
                    : 'text-sm font-medium text-content-tertiary hover:text-content-primary'
                }
              >
                Settings
              </Link>
            )}
          </nav>
          <div className="flex items-center gap-3">
            {/*
              U7 (E3) — the way back to a flow the rep left mid-edit. Renders
              nothing unless unsaved work exists somewhere OTHER than the page
              currently on screen.
            */}
            <UnsavedEditsPill />
            <ThemeToggle />
          </div>
        </header>

        {/*
          Stable, targetable main-content container: an open `PushDrawer` puts the
          push-padding class on this element (`data-playbooks-main`), which is
          also the handle U4's browser verification reads.
        */}
        <main data-playbooks-main data-drawer-open={isDrawerOpen || undefined} className={mainClassName}>
          <Outlet />
        </main>

        {/*
          U1 — the "Where is this used" sidebar, mounted ONCE here (not
          per-page) so it survives every route change. Renders
          nothing while `usage-sidebar-store.ts`'s `pending` is `null`.
        */}
        <UsageSidebar />

        <GlobalGlazeToastRegion />
      </div>
    </GlazeModalProvider>
  );
}
